import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import NavbarV2 from '../components/navbar-v2';
import FooterV2 from '../components/footer-v2';
import CihedLogo from '../assets/images/cihed-takvim-logo.png';
import '../assets/css/cuz-al.css';
import {
  ReadingMode,
  ReadingType,
  CizelgeSatiri,
  modeLabel,
  typeLabel,
  cuzSayfalari,
  cizelgeSatirSayisi,
  okumaCizelgesi,
  cizelgeAciklamasi,
  kisaTarih,
  tarihAraligi,
  cuzSiralama,
  AY_KISA,
  AY_UZUN
} from '../lib/cuzAl';

interface LocationState {
  hatimAdi: string;
  cuzler: number[];
  ad: string;
  soyad: string;
  okumaTuru: 'donerli' | 'sabit';
  readingMode: ReadingMode;
  readingType?: ReadingType;
  baslangicTarihi?: string;
  bitisTarihi?: string;
}

const PAYLASIM_LINKI = 'https://hatimtakip.com.tr/cuzal';
const HADIS = "Kur'an okuyan ve onunla amel eden kimseye kıyamet günü, Kur'an şefaatçi olarak gelir.";
const TAKVIM_HAFTA = 6; // takvimde bir sayfada gösterilen hafta sayısı
const HAFTA_GUNLERI = ['PZT', 'SAL', 'ÇAR', 'PER', 'CUM', 'CMT', 'PAZ'];

const sayfaMetni = (cuz: number) => {
  const s = cuzSayfalari(cuz);
  return `${s.baslangic}–${s.bitis}`;
};

const gunEkle = (d: Date, n: number) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
};

const gunFarki = (a: Date, b: Date) => Math.round((a.getTime() - b.getTime()) / 864e5);

interface TakvimHucresi {
  tarih: Date;
  satir: CizelgeSatiri | null;
  ilk: boolean;
}

// Okuma süresini kapsayan, pazartesiden başlayan haftalar
const takvimHaftalari = (cizelge: CizelgeSatiri[], gunluk: boolean): TakvimHucresi[][] => {
  if (!cizelge.length) return [];
  const bas = gunEkle(cizelge[0].tarih, 0);
  const toplamGun = gunluk ? cizelge.length : cizelge.length * 7;
  const gridBas = gunEkle(bas, -((bas.getDay() + 6) % 7));
  const son = gunEkle(bas, toplamGun - 1);
  const gridSon = gunEkle(son, (7 - son.getDay()) % 7);
  const haftalar: TakvimHucresi[][] = [];
  for (let d = gridBas; d <= gridSon; d = gunEkle(d, 1)) {
    if ((d.getDay() + 6) % 7 === 0) haftalar.push([]);
    const idx = gunFarki(d, bas);
    const icinde = idx >= 0 && idx < toplamGun;
    haftalar[haftalar.length - 1].push({
      tarih: d,
      satir: icinde ? cizelge[gunluk ? idx : Math.floor(idx / 7)] : null,
      ilk: icinde && (gunluk ? idx === 0 : idx % 7 === 0)
    });
  }
  return haftalar;
};

const CuzBasari: React.FC = () => {
  const location = useLocation();
  const { hatimId } = useParams();
  const state = location.state as LocationState | null;

  const [sekme, setSekme] = useState(0);
  const [gorunum, setGorunum] = useState<'takvim' | 'liste'>('takvim');
  const [takvimSayfa, setTakvimSayfa] = useState(0);
  const [wantsReminder, setWantsReminder] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);
  const [cizelgeIndiriliyor, setCizelgeIndiriliyor] = useState(false);
  const [kartIndiriliyor, setKartIndiriliyor] = useState(false);

  const kartRef = useRef<HTMLDivElement>(null);
  const cizelgeRefs = useRef<(HTMLDivElement | null)[]>([]);

  const cuzler = state?.cuzler || [];
  const gunluk = state?.readingType === 'daily';
  const readingMode: ReadingMode = state?.readingMode || 'fixed';
  const satirSayisi = cizelgeSatirSayisi(gunluk, state?.baslangicTarihi, state?.bitisTarihi);

  const cizelgeler = useMemo(
    () => cuzler.map((cuzNo) => ({
      cuzNo,
      satirlar: okumaCizelgesi(cuzNo, { mode: readingMode, gunluk, baslangic: state?.baslangicTarihi, satirSayisi })
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cuzler.join(','), readingMode, gunluk, state?.baslangicTarihi, satirSayisi]
  );

  const aktif = cizelgeler[Math.min(sekme, cizelgeler.length - 1)];
  const haftalar = useMemo(() => (aktif ? takvimHaftalari(aktif.satirlar, gunluk) : []), [aktif, gunluk]);
  const sayfaSayisi = Math.max(1, Math.ceil(haftalar.length / TAKVIM_HAFTA));
  const gorunenHaftalar = haftalar.slice(takvimSayfa * TAKVIM_HAFTA, (takvimSayfa + 1) * TAKVIM_HAFTA);

  useEffect(() => {
    if (!kopyalandi) return;
    const t = setTimeout(() => setKopyalandi(false), 2500);
    return () => clearTimeout(t);
  }, [kopyalandi]);

  if (!state || !state.hatimAdi) {
    return (
      <>
        <NavbarV2 />
        <div className="ht-cuzal ht-basari">
          <div className="ht-wrap">
            <div className="ht-empty">
              <h2>Sayfa Bulunamadı</h2>
              <Link to="/#cuz-al" className="ht-btn ht-btn-gold">Cüz Al Sayfasına Dön</Link>
            </div>
          </div>
        </div>
        <FooterV2 />
      </>
    );
  }

  const { hatimAdi, ad, soyad } = state;
  const okumaTuruMetni = `${modeLabel(readingMode)} · ${typeLabel(state.readingType || 'weekly')}`;
  const aralik = tarihAraligi(state.baslangicTarihi, state.bitisTarihi);
  const cuzlerKisa = cuzler.length === 1 ? `${cuzler[0]}. Cüz` : `${cuzler.join(', ')}. Cüzler`;
  const paylasimBasligi = `${hatimAdi} hatimine ${cuzSiralama(cuzler)} ${cuzler.length === 1 ? 'cüzü' : 'cüzleri'} aldım!`;
  const gorunenCuzler = cuzler.slice(0, 5);
  const fazlaCuz = cuzler.length - gorunenCuzler.length;

  const pngIndir = (canvas: HTMLCanvasElement, dosyaAdi: string) => {
    const link = document.createElement('a');
    link.download = dosyaAdi;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleDownloadCard = async () => {
    setKartIndiriliyor(true);
    await new Promise((r) => setTimeout(r, 50));
    try {
      if (!kartRef.current) throw new Error('kart yok');
      const canvas = await html2canvas(kartRef.current, { backgroundColor: '#0B3A44', scale: 1, logging: false });
      pngIndir(canvas, `hatim-cuzler-${cuzler.join('-')}.png`);
    } catch (error) {
      console.error('Kart indirilemedi:', error);
      alert('Kart indirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setKartIndiriliyor(false);
    }
  };

  // Her cüz için ayrı görsel (B4)
  const handleDownloadSchedule = async () => {
    setCizelgeIndiriliyor(true);
    await new Promise((r) => setTimeout(r, 50));
    try {
      for (let i = 0; i < cizelgeler.length; i++) {
        const el = cizelgeRefs.current[i];
        if (!el) continue;
        const canvas = await html2canvas(el, { backgroundColor: '#F8FDFE', scale: 1, logging: false, useCORS: true });
        pngIndir(canvas, `okuma-cizelgesi-${hatimAdi.replace(/\s+/g, '-')}-${cizelgeler[i].cuzNo}-cuz.png`);
        await new Promise((r) => setTimeout(r, 300));
      }
    } catch (error) {
      console.error('Çizelge indirilemedi:', error);
      alert('Çizelge indirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setCizelgeIndiriliyor(false);
    }
  };

  const handleWhatsAppShare = () => {
    const cuzText = cuzler.length === 1 ? `${cuzler[0]}. cüzü` : `${cuzler.join(', ')}. cüzleri`;
    const text = `🕌 ${hatimAdi} hatimine ${cuzText} aldım!\n\nSiz de katılmak için: ${PAYLASIM_LINKI}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(PAYLASIM_LINKI);
      setKopyalandi(true);
    } catch {
      alert(`Link: ${PAYLASIM_LINKI}`);
    }
  };

  const sekmeSec = (i: number) => {
    setSekme(i);
    setTakvimSayfa(0);
  };

  const sayfaAraligi = () => {
    const ilk = gorunenHaftalar[0]?.[0]?.tarih;
    const son = gorunenHaftalar[gorunenHaftalar.length - 1]?.[6]?.tarih;
    if (!ilk || !son) return '';
    return `${ilk.getDate()} ${AY_KISA[ilk.getMonth()]} – ${son.getDate()} ${AY_KISA[son.getMonth()]} ${son.getFullYear()}`;
  };

  return (
    <>
      <NavbarV2 />
      <div className="ht-cuzal ht-basari">
        {/* Tebrikler */}
        <section className="ht-wrap ht-bhero">
          <span className="ht-bhero-check" aria-hidden="true"><CheckIcon /></span>
          <h1>Tebrikler, {ad}!</h1>
          <p className="ht-bhero-sub">
            {cuzler.length === 1 ? 'Cüz başarıyla alındı' : `${cuzler.length} cüz başarıyla alındı`}:{' '}
            <strong>{cuzSiralama(cuzler)} cüz</strong>
          </p>
          <p className="ht-bhero-dua">Allah kabul etsin, hayırlı okumalar dileriz</p>
          <div className="ht-chips ht-bhero-chips">
            <span className="ht-chip ht-chip-line ht-chip-strong">{hatimAdi}</span>
            <span className="ht-chip ht-chip-green">{modeLabel(readingMode)} Hatim</span>
            <span className="ht-chip ht-chip-gold">{typeLabel(state.readingType || 'weekly')}</span>
            {aralik && <span className="ht-chip ht-chip-line">{aralik}</span>}
          </div>
        </section>

        <div className="ht-wrap ht-main">
          {/* Okuma çizelgesi */}
          <div className="ht-panel-col">
            <div className="ht-panel ht-cal-card">
              <div className="ht-cal-head">
                <div className="ht-cal-title">
                  <h2>Okuma Çizelgeniz</h2>
                  <p>{cizelgeAciklamasi(readingMode, gunluk)}</p>
                </div>
                <div className="ht-seg" role="group" aria-label="Görünüm">
                  <button type="button" className={gorunum === 'takvim' ? 'is-on' : ''} aria-pressed={gorunum === 'takvim'} onClick={() => setGorunum('takvim')}>Takvim</button>
                  <button type="button" className={gorunum === 'liste' ? 'is-on' : ''} aria-pressed={gorunum === 'liste'} onClick={() => setGorunum('liste')}>Liste</button>
                </div>
              </div>

              {cuzler.length > 1 && (
                <div className="ht-htabs-row" role="group" aria-label="Cüz seçimi">
                  {cizelgeler.map((c, i) => (
                    <button
                      key={c.cuzNo}
                      type="button"
                      className={`ht-htab${i === sekme ? ' is-on' : ''}`}
                      aria-pressed={i === sekme}
                      onClick={() => sekmeSec(i)}
                    >
                      {readingMode === 'rotational' ? `${c.cuzNo}. Cüz ile başlayan` : `${c.cuzNo}. Cüz`}
                    </button>
                  ))}
                </div>
              )}

              {gorunum === 'takvim' ? (
                <div className="ht-cal-wrap">
                  {sayfaSayisi > 1 && (
                    <div className="ht-cal-pager">
                      <button type="button" className="ht-pager-btn" onClick={() => setTakvimSayfa((p) => p - 1)} disabled={takvimSayfa === 0} aria-label="Önceki haftalar">‹</button>
                      <span>{sayfaAraligi()}</span>
                      <button type="button" className="ht-pager-btn" onClick={() => setTakvimSayfa((p) => p + 1)} disabled={takvimSayfa >= sayfaSayisi - 1} aria-label="Sonraki haftalar">›</button>
                    </div>
                  )}
                  <div className="ht-cal" aria-hidden="true">
                    {HAFTA_GUNLERI.map((g) => <span key={g} className="ht-wd">{g}</span>)}
                  </div>
                  <div className="ht-cal">
                    {gorunenHaftalar.flat().map((h, i) => {
                      const ayGoster = i === 0 || h.tarih.getDate() === 1;
                      return (
                        <div
                          key={h.tarih.getTime()}
                          className={`ht-day ${h.satir ? 'is-in' : 'is-out'}${h.ilk ? ' is-first' : ''}`}
                          aria-label={`${h.tarih.getDate()} ${AY_UZUN[h.tarih.getMonth()]}${h.satir ? `, ${h.satir.cuz}. cüz` : ''}`}
                        >
                          <span className="ht-day-num">{h.tarih.getDate()}{ayGoster ? ` ${AY_KISA[h.tarih.getMonth()]}` : ''}</span>
                          {h.satir && (
                            <>
                              <span className="ht-day-cuz">{h.satir.cuz}.<span className="ht-day-cuz-word"> Cüz</span></span>
                              <span className="ht-day-pg">s. {sayfaMetni(h.satir.cuz)}</span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="ht-list">
                  <div className="ht-lrow ht-lrow-head">
                    <span>{gunluk ? 'GÜN' : 'HAFTA'}</span><span>TARİH</span><span>CÜZ</span><span className="ht-lpg">SAYFA</span>
                  </div>
                  {aktif?.satirlar.map((r) => (
                    <div key={r.sira} className="ht-lrow">
                      <span className="ht-lrow-gun">{r.etiket}</span>
                      <span className="ht-lrow-tarih">{kisaTarih(r.tarih)}</span>
                      <span className="ht-lrow-cuz">{r.cuz}. Cüz</span>
                      <span className="ht-lpg">{sayfaMetni(r.cuz)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="ht-cal-foot">
                <span>
                  {satirSayisi} {gunluk ? 'gün' : 'hafta'}
                  {gorunum === 'takvim' && (gunluk ? ' · Turkuaz çerçeve: ilk okuma günü' : ' · Turkuaz çerçeve: haftanın ilk okuma günü')}
                </span>
                <button type="button" className="ht-btn ht-btn-gold" onClick={handleDownloadSchedule} disabled={cizelgeIndiriliyor}>
                  <DownloadIcon />
                  {cizelgeIndiriliyor
                    ? 'Hazırlanıyor...'
                    : cuzler.length > 1 ? `Tüm Çizelgeleri İndir (${cuzler.length} Cüz)` : 'Okuma Çizelgesini İndir'}
                </button>
              </div>
            </div>
          </div>

          {/* Paylaş + Hatırlatma */}
          <aside className="ht-side">
            <div className="ht-panel ht-share">
              <h2>Paylaş ve Davet Et</h2>
              <div className="ht-share-card">
                <Halkalar className="ht-share-rings" boyut={220} />
                <div className="ht-share-cuzler">
                  {gorunenCuzler.map((c) => <span key={c}>{c}</span>)}
                  {fazlaCuz > 0 && <span className="is-more">+{fazlaCuz}</span>}
                </div>
                <div className="ht-share-title">{paylasimBasligi}</div>
                <div className="ht-share-info">
                  <div><small>Okuyucu</small><b>{ad} {soyad}</b></div>
                  <div><small>Okuma Türü</small><b>{okumaTuruMetni}</b></div>
                </div>
                <p className="ht-share-hadis">"{HADIS}" <span>— Hz. Muhammed (s.a.v)</span></p>
                <div className="ht-share-site">hatimtakip.com.tr</div>
              </div>
              <div className="ht-share-actions">
                <button type="button" className="ht-btn ht-btn-gold" onClick={handleWhatsAppShare}>
                  <WhatsAppIcon />{"WhatsApp'ta Paylaş"}
                </button>
                <div className="ht-share-row">
                  <button type="button" className="ht-btn ht-btn-line" onClick={handleDownloadCard} disabled={kartIndiriliyor}>
                    <DownloadIcon />{kartIndiriliyor ? 'Hazırlanıyor...' : 'Kartı İndir'}
                  </button>
                  <button type="button" className="ht-btn ht-btn-line" onClick={handleCopyLink}>
                    <LinkIcon />{kopyalandi ? 'Link kopyalandı!' : 'Linki Kopyala'}
                  </button>
                </div>
              </div>
            </div>

            <div className="ht-panel ht-remind">
              <div className="ht-remind-top">
                <div className="ht-remind-info">
                  <span className="ht-remind-icon" aria-hidden="true"><BellIcon /></span>
                  <div>
                    <label htmlFor="ht-hatirlatma">Hatırlatma almak istiyorum</label>
                    <small>Günlük Kuran okuma hatırlatıcısı</small>
                  </div>
                </div>
                <span className="ht-switch">
                  <input id="ht-hatirlatma" type="checkbox" checked={wantsReminder} onChange={(e) => setWantsReminder(e.target.checked)} />
                  <span />
                </span>
              </div>
              {wantsReminder && (
                <div className="ht-remind-box">
                  <p>HatimTakip mobil uygulamasından bildirim ayarlarınızı yapabilirsiniz. Günlük hatırlatıcı için uygulamayı indirin.</p>
                  <div className="ht-share-row">
                    <a className="ht-btn ht-btn-line" href="https://apps.apple.com/tr/app/hatim-takip-uygulamas%C4%B1/id6756719731?l=tr" target="_blank" rel="noopener noreferrer">App Store</a>
                    <a className="ht-btn ht-btn-line" href="https://play.google.com/store/apps/details?id=com.sametgdr.hatimtakipapp&hl=tr" target="_blank" rel="noopener noreferrer">Google Play</a>
                  </div>
                </div>
              )}
            </div>

            <div className="ht-share-row">
              <Link to={hatimId ? `/?grup=${hatimId}#cuz-al` : '/#cuz-al'} className="ht-btn ht-btn-gold">Başka Cüz Al</Link>
              <Link to="/" className="ht-btn ht-btn-line">Ana Sayfaya Dön</Link>
            </div>
          </aside>
        </div>
      </div>
      <FooterV2 />

      {/* İndirilecek görseller: yalnızca indirme sırasında, ekran dışında çizilir */}
      {(kartIndiriliyor || cizelgeIndiriliyor) && (
        <div className="ht-offscreen" aria-hidden="true">
          {kartIndiriliyor && (
            <div ref={kartRef} className="ht-png-card">
              <Halkalar className="ht-png-card-rings" boyut={760} />
              <Marka boyut={52} yazi={34} />
              <div className="ht-png-card-mid">
                <div className="ht-png-card-cuzler">
                  {gorunenCuzler.map((c) => <span key={c}>{c}</span>)}
                  {fazlaCuz > 0 && <span className="is-more">+{fazlaCuz}</span>}
                </div>
                <h1 style={{ fontSize: paylasimBasligi.length > 60 ? 52 : 68 }}>{paylasimBasligi}</h1>
                <div className="ht-png-card-info">
                  <div><small>Okuyucu</small><b>{ad} {soyad}</b></div>
                  <div><small>Okuma Türü</small><b>{okumaTuruMetni}</b></div>
                  <div><small>Seçilen Cüzler</small><b className="is-gold">{cuzlerKisa}</b></div>
                </div>
              </div>
              <div className="ht-png-card-foot">
                <p><i>"{HADIS}"</i><br /><span>Hz. Muhammed (s.a.v)</span></p>
                <div><small>Siz de katılın</small><b>hatimtakip.com.tr/cuzal</b></div>
              </div>
            </div>
          )}
          {cizelgeIndiriliyor && cizelgeler.map((c, i) => {
            const yari = Math.ceil(c.satirlar.length / 2);
            return (
              <div key={c.cuzNo} ref={(el) => { cizelgeRefs.current[i] = el; }} className="ht-png-sheet">
                <div className="ht-png-sheet-head">
                  <div className="ht-png-sheet-top">
                    <Marka boyut={48} yazi={30} />
                    <span className="ht-png-sheet-tag">OKUMA ÇİZELGESİ</span>
                  </div>
                  <div className="ht-png-sheet-title">
                    <div>
                      <span className="ht-png-sheet-name">{ad} {soyad}</span>
                      <span className="ht-png-sheet-hatim">{hatimAdi}</span>
                      <span className="ht-png-sheet-meta">{modeLabel(readingMode)} Hatim · {typeLabel(state.readingType || 'weekly')}{aralik ? ` · ${aralik}` : ''}</span>
                    </div>
                    <div className="ht-png-sheet-badge">
                      <b>{c.cuzNo}</b>
                      <span>{readingMode === 'rotational' ? 'Cüz ile başlar' : 'Cüz'}</span>
                    </div>
                  </div>
                </div>
                <div className="ht-png-sheet-cols">
                  {[c.satirlar.slice(0, yari), c.satirlar.slice(yari)].map((kolon, k) => (
                    <div key={k}>
                      <div className="ht-png-row ht-png-row-head">
                        <span>{gunluk ? 'GÜN' : 'HAFTA'}</span><span>TARİH</span><span>CÜZ</span><span>SAYFA</span>
                      </div>
                      {kolon.map((r, j) => (
                        <div key={r.sira} className={`ht-png-row${j % 2 ? ' is-alt' : ''}`}>
                          <span className="is-gun">{r.etiket}</span>
                          <span>{kisaTarih(r.tarih)}</span>
                          <b>{r.cuz}. Cüz</b>
                          <span className="is-pg">{sayfaMetni(r.cuz)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="ht-png-sheet-foot">
                  <p>{cizelgeAciklamasi(readingMode, gunluk)}</p>
                  <div>
                    <img src={CihedLogo} alt="ÇİHED" />
                    <b>hatimtakip.com.tr</b>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const Marka: React.FC<{ boyut: number; yazi: number }> = ({ boyut, yazi }) => (
  <div className="ht-marka">
    <svg width={boyut} height={boyut} viewBox="0 0 34 34" fill="none" aria-hidden="true">
      <circle cx="17" cy="17" r="15.5" stroke="#4FDBEB" strokeWidth="1.5" />
      <path d="M21.5 9.5a8 8 0 1 0 0 15 9.5 9.5 0 0 1 0-15z" fill="#4FDBEB" />
    </svg>
    <span style={{ fontSize: yazi }}>Hatim<span>Takip</span></span>
  </div>
);

const Halkalar: React.FC<{ className: string; boyut: number }> = ({ className, boyut }) => (
  <svg className={className} aria-hidden="true" width={boyut} height={boyut} viewBox="0 0 220 220" fill="none">
    <circle cx="110" cy="110" r="108" stroke="#4FDBEB" strokeOpacity="0.25" />
    <circle cx="110" cy="110" r="80" stroke="#4FDBEB" strokeOpacity="0.18" />
    <circle cx="110" cy="110" r="52" stroke="#4FDBEB" strokeOpacity="0.12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 4v11M7 10l5 5 5-5M5 20h14" /></svg>
);

const LinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" /></svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
);

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.8 14.05c-.24.68-1.42 1.31-1.97 1.39-.5.08-1.14.11-1.84-.12-.42-.13-.97-.31-1.66-.61-2.92-1.26-4.83-4.2-4.98-4.39-.14-.2-1.19-1.58-1.19-3.01 0-1.43.75-2.14 1.02-2.43.27-.29.58-.36.78-.36h.56c.18 0 .42-.07.66.5.24.58.83 2.01.9 2.16.07.15.12.32.02.51-.1.2-.15.32-.29.49-.15.17-.31.38-.44.51-.15.15-.3.3-.13.6.17.29.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.35 1.46.29.15.46.12.63-.07.17-.2.73-.85.92-1.14.2-.29.39-.24.66-.15.27.1 1.7.8 1.99.95.29.15.49.22.56.34.07.12.07.7-.17 1.38z" /></svg>
);

export default CuzBasari;
